import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { Motif } from "./Motif";

const stappen = [
  {
    n: "01",
    title: "Eerste gesprek",
    text: "Tijdens een eerste gesprek maken we ruimte om elkaar te leren kennen. Ik tracht een zo volledig mogelijk beeld te krijgen van jullie bezorgdheden en klachten, maar ook van de krachten die al aanwezig zijn. We bespreken het doel van de begeleiding.",
  },
  {
    n: "02",
    title: "Vervolggesprekken",
    text: "Tijdens de vervolggesprekken werken we samen naar een oplossing toe om jullie welzijn te optimaliseren.",
  },
  {
    n: "03",
    title: "Duur van de begeleiding",
    text: "We werken samen zolang als nodig is. Dat hoeft niet per se kort of lang te zijn — we bekijken dit samen binnen jullie specifieke situatie.",
  },
];

const tarieven = [
  { label: "Individueel gesprek", detail: "50 min", price: "€ 70" },
  { label: "Langer gesprek", detail: "90 min", price: "€ 110" },
  { label: "Online gesprek", detail: "50 min", price: "€ 70" },
  {
    label: "Gesprek op verplaatsing",
    detail: "klik voor details",
    price: "€ 90",
    info: "Gesprek op verplaatsing (thuis, op school, externe diensten, …): € 90 voor het eerste uur en € 45 per begonnen half uur. Voor verplaatsingen wordt een kilometervergoeding van € 0,50 per gereden km (heen en terug vanaf de praktijk) aangerekend.",
  },
  { label: "Overleg per telefoon of mail", detail: "per begonnen kwartier", price: "€ 20" },
  { label: "Verslaggeving à rato van tijd", detail: "per begonnen kwartier", price: "€ 20" },
];

export const Werkwijze = () => {
  const [openTarief, setOpenTarief] = useState(-1);
  return (
  <section id="werkwijze" className="relative bg-[#EFEBE1] py-16 md:py-32 overflow-hidden">
    <div className="pointer-events-none absolute -right-24 top-24 w-[320px] h-[320px]">
      <Motif opacity={0.12} flip />
    </div>

    <div className="relative max-w-7xl mx-auto px-5 md:px-8">
      <Reveal className="mb-14 max-w-2xl">
        <SectionLabel className="mb-5">Werkwijze &amp; tarieven</SectionLabel>
        <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-kern-dark">
          Zo werken we samen.
        </h2>
      </Reveal>

      {/* Werkwijze */}
      <div className="relative grid md:grid-cols-3 gap-6 mb-8">
        {stappen.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.1}>
            <div
              data-testid={`werkwijze-step-${i}`}
              className="h-full bg-kern-bg rounded-[32px] p-6 md:p-8 shadow-[0_10px_40px_rgba(79,80,72,0.05)]"
            >
              <div className="font-serif text-5xl text-kern-sage/60 leading-none mb-4">{s.n}</div>
              <h3 className="font-serif text-2xl text-kern-dark mb-3">{s.title}</h3>
              <p className="text-sm text-kern-taupe leading-relaxed">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mb-20 md:mb-28">
        <p className="text-sm text-kern-taupe leading-relaxed max-w-2xl">
          Gesprekken gaan door in de praktijk te Sint-Lenaarts. Op aanvraag kan dit ook
          op verplaatsing of online.
        </p>
      </Reveal>

      {/* Tarieven */}
      <Reveal>
        <div className="relative overflow-hidden rounded-[48px] bg-kern-bg px-6 md:px-12 py-10 md:py-16 shadow-[0_20px_60px_rgba(79,80,72,0.06)]">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-10">
            <div className="lg:col-span-2">
              <SectionLabel className="mb-5">Tarieven</SectionLabel>
              <h3 className="font-serif text-3xl text-kern-dark leading-tight">
                Duidelijk en eerlijk.
              </h3>
              <p className="mt-4 text-sm text-kern-taupe leading-relaxed">
                Een gesprek duurt 50 minuten. Op aanvraag plannen we een langer gesprek,
                een online gesprek of een gesprek op verplaatsing.
              </p>
            </div>

            <div className="lg:col-span-3">
              <div className="space-y-3">
                {tarieven.map((t, i) => {
                  const isOpen = openTarief === i;
                  return (
                    <div
                      key={t.label}
                      data-testid={`tarief-${i}`}
                      className="bg-white rounded-2xl overflow-hidden shadow-[0_6px_20px_rgba(79,80,72,0.04)] hover:shadow-[0_16px_44px_rgba(79,80,72,0.1)] transition-shadow duration-300"
                    >
                      <div
                        onClick={t.info ? () => setOpenTarief(isOpen ? -1 : i) : undefined}
                        {...(t.info
                          ? {
                              role: "button",
                              "data-testid": `tarief-toggle-${i}`,
                              "aria-expanded": isOpen,
                            }
                          : {})}
                        className={`flex items-center justify-between gap-4 px-5 py-4 md:px-6 md:py-5 ${
                          t.info ? "cursor-pointer" : ""
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <span className="w-9 h-9 rounded-full bg-kern-sage/25 flex items-center justify-center shrink-0">
                            <Check size={16} className="text-kern-olive" />
                          </span>
                          <div>
                            <div className="font-medium text-kern-dark">{t.label}</div>
                            <div className="text-xs text-kern-taupe">{t.detail}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="font-serif text-2xl text-kern-terracotta whitespace-nowrap">
                            {t.price}
                          </div>
                          {t.info && (
                            <ChevronDown
                              size={18}
                              className={`text-kern-taupe shrink-0 transition-transform duration-300 ${
                                isOpen ? "rotate-180" : ""
                              }`}
                            />
                          )}
                        </div>
                      </div>
                      {t.info && (
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              key="info"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                              className="overflow-hidden"
                            >
                              <p
                                data-testid={`tarief-info-${i}`}
                                className="px-5 md:px-6 pb-4 md:pb-5 text-xs text-kern-taupe leading-relaxed"
                              >
                                {t.info}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 space-y-2 text-xs text-kern-taupe leading-relaxed">
                <p>
                  Er kan contant of met Payconiq betaald worden. Gesprekken worden na
                  iedere consultatie afgerekend.
                </p>
                <p>
                  Een afspraak annuleren kan kosteloos tot 48u voor je geplande afspraak,
                  telefonisch of per sms. Indien je minder dan 48 uur op voorhand
                  annuleert, wordt 50% van de geplande afspraak aangerekend als
                  tegemoetkoming. Bij annuleringen binnen de 24u betaal je de volledige
                  afspraak. Hier worden geen uitzonderingen op gemaakt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
